import { sweetalert } from "../App";
import SamplesAPI from "../network/SamplesAPI";
import moment from "moment-timezone";
import { MAX_DOCUMENTS_PER_DOWNLOAD, REPORT_TYPE_LABELS, ReportType, SAMPLE_DOCUMENT_OPTIONS, SampleDocumentType } from "../types/ReportDownload";
import { escapeHTML } from "./FormatUtil";

// Checkbox picker for which documents to pull. Resolves to the checked types in picker order,
// or null if the user closed it.
export const promptDocumentSelection = async (title: string): Promise<SampleDocumentType[] | null> => {
    const checkbox = (id: string, label: string, value = "") =>
        `<div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="${id}" value="${value}">
            <label class="form-check-label" for="${id}">${label}</label>
        </div>`;
    const result = await sweetalert.fire({
        title: escapeHTML(title),
        html: `<div class="text-left d-inline-block">
            ${checkbox("sample-doc-all", "<b>Select All</b>")}
            <hr class="my-2"/>
            ${SAMPLE_DOCUMENT_OPTIONS.map(o => checkbox(`sample-doc-${o.type}`, o.label, o.type)).join("")}
        </div>`,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Download",
        allowOutsideClick: false,
        didOpen: (popup) => {
            // The picker opens over Bootstrap modals, whose focus trap pulls focus back on any document
            // focusin; keep the popup's focus events to itself so keyboard users can reach the checkboxes.
            popup.addEventListener("focusin", (e) => e.stopPropagation());
            const selectAll = popup.querySelector<HTMLInputElement>("#sample-doc-all");
            const options = Array.from(popup.querySelectorAll<HTMLInputElement>("input[id^='sample-doc-']:not(#sample-doc-all)"));
            selectAll.addEventListener("change", () => options.forEach(o => { o.checked = selectAll.checked; }));
            options.forEach(o => o.addEventListener("change", () => { selectAll.checked = options.every(x => x.checked); }));
        },
        preConfirm: () => {
            const checked = SAMPLE_DOCUMENT_OPTIONS
                .map(o => o.type)
                .filter(type => (document.getElementById(`sample-doc-${type}`) as HTMLInputElement)?.checked);
            if (checked.length === 0) {
                sweetalert.showValidationMessage("Select at least one document.");
                return false;
            }
            return checked;
        },
    });
    return result.isConfirmed ? result.value as SampleDocumentType[] : null;
}

// "A", "A and B", "A, B and C"
const joinNames = (names: string[]) =>
    names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// Alert listing the documents that weren't available, one line per sample, e.g.
// "ME123: Preliminary Report and Send Out Report".
const showUnavailableDocuments = (skipped: string[], icon: "error" | "info") => {
    const bySample: { [reqNum: string]: string[] } = {};
    skipped.forEach(entry => {
        const separator = entry.lastIndexOf(":");
        const reqNum = entry.slice(0, separator);
        const type = entry.slice(separator + 1);
        (bySample[reqNum] = bySample[reqNum] || []).push(type);
    });
    // Report names are bold; they come from SAMPLE_DOCUMENT_OPTIONS, so they're safe to use as HTML
    const namesFor = (types: string[]) => SAMPLE_DOCUMENT_OPTIONS.filter(o => types.includes(o.type)).map(o => `<b>${o.documentName}</b>`);
    const reqNums = Object.keys(bySample);
    const list = reqNums.map(r => `<li><b>${escapeHTML(r)}</b>: ${joinNames(namesFor(bySample[r]))}</li>`).join("");
    return sweetalert.fire({
        icon: icon,
        title: "",
        width: "48em",
        html: `<div>The following documents are not available:<ul class="list-unstyled mt-2 mb-0" style="max-height: 200px; overflow-y: auto;">${list}</ul></div>`,
    });
}

// True (and tells the user) when samples x document types is over the per-download limit,
// so an oversized request never reaches STARLIMS. The backend enforces the same limit.
export const exceedsDocumentLimit = (reqNums: string[], documentTypes: SampleDocumentType[]): boolean => {
    const documentCount = reqNums.length * documentTypes.length;
    if (documentCount <= MAX_DOCUMENTS_PER_DOWNLOAD) {
        return false;
    }
    sweetalert.fire({
        icon: "error",
        title: "",
        text: `Downloads are limited to ${MAX_DOCUMENTS_PER_DOWNLOAD} documents at a time (samples x document types). ${reqNums.length} samples x ${documentTypes.length} document types is ${documentCount}. Please select fewer samples or documents.`,
    });
    return true;
}

// Warns before re-downloading documents that were already viewed. Resolves true to go ahead with the
// download, false if the user cancelled. Human samples only; animal sample downloads aren't tracked.
// setLoading shows the caller's spinner while the history is fetched, so the screen doesn't sit idle. It
// stays on when the download goes ahead (the caller turns it off after the download) and only turns off
// for the warning, so the screen doesn't flash between the check and the download.
export const confirmAlreadyViewed = async (reqNums: string[], documentTypes: SampleDocumentType[], setLoading?: (loading: boolean) => void): Promise<boolean> => {
    const trackedTypes: ReportType[] = SAMPLE_DOCUMENT_OPTIONS
        .filter(o => documentTypes.includes(o.type))
        .flatMap(o => o.logTypes);
    let alreadyViewed;
    setLoading?.(true);
    try {
        const response = await SamplesAPI.getReportDownloadSummary(reqNums);
        if (!response.success) {
            // Don't block the download if the history can't be checked
            console.error("getReportDownloadSummary failed", response.reason);
            return true;
        }
        alreadyViewed = (response.data || []).filter(s => trackedTypes.includes(s.ReportType));
    } catch (e) {
        console.error("getReportDownloadSummary error", e);
        return true;
    }
    if (alreadyViewed.length === 0) {
        return true;
    }
    setLoading?.(false);
    const tz = moment.tz.guess();
    const list = alreadyViewed
        .map(s => `<li><b>${escapeHTML(s.ReqNum)}</b> ${REPORT_TYPE_LABELS[s.ReportType]} - last viewed ${moment.tz(s.LastDownloadedDate, tz).format('MM/DD/YYYY h:mm A')}${s.LastDownloadedBy ? ` by ${escapeHTML(s.LastDownloadedBy)}` : ""}</li>`)
        .join("");
    const confirm = await sweetalert.fire({
        icon: "warning",
        title: "Already viewed",
        width: "48em",
        html: `<div>Some of the selected documents were already viewed:<ul class="list-unstyled mt-2" style="max-height: 200px; overflow-y: auto;">${list}</ul>Download anyway?</div>`,
        showCancelButton: true,
        confirmButtonText: "Download Anyway",
        cancelButtonText: "Cancel",
    });
    return confirm.isConfirmed;
}

// Pulls the selected documents for the samples as one merged PDF and opens it in a new tab.
// Resolves to the ReqNums that had at least one document included (empty on failure).
export const downloadSampleDocuments = async (reqNums: string[], documentTypes: SampleDocumentType[]): Promise<string[]> => {
    if (exceedsDocumentLimit(reqNums, documentTypes)) {
        return [];
    }
    try {
        const result = await SamplesAPI.downloadSampleDocuments(reqNums, documentTypes);
        const skipped = result.skippedDocuments;
        if (!result.blob) {
            console.error("downloadSampleDocuments failed", result.error);
            if (skipped.length > 0) {
                showUnavailableDocuments(skipped, "error");
            } else {
                sweetalert.fire({ icon: "error", title: "", text: result.error?.reason || "Could not retrieve documents." });
            }
            return [];
        }
        const data = await result.blob.arrayBuffer();
        window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");

        if (skipped.length > 0) {
            showUnavailableDocuments(skipped, "info");
        }
        // A sample counts as downloaded unless every one of its documents was skipped
        return reqNums.filter(r => documentTypes.some(t => !skipped.includes(`${r}:${t}`)));
    } catch (error) {
        console.error("error retrieving sample documents", error);
        sweetalert.fire({ icon: "error", title: "", text: "Could not retrieve documents. Please try again." });
        return [];
    }
}
